// src/pages/CustomerLoanPayments.js

import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Button, 
    Paper, 
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    Menu,
    Avatar,
    CircularProgress,
    Tooltip,
    Alert,
    Snackbar,
    InputAdornment,
    Tabs,
    Tab,
    Card,
    CardContent,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { 
    Delete, 
    Search,
    Receipt,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart, 
    GetApp,
    Print,
    Person,
    Phone,
    CalendarMonth,
    AccountBalance,
    Money,
    MoneyOff,
    TrendingUp,
    TrendingDown,
    LocalAtm,
    Paid,
    Payment,
    AttachMoney,
    History,
    Description,
    Note,
    Add as AddIcon,
    MoreVert
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APICustomerLoanPaymentsList,
    APICustomerLoanPaymentsStats,
    APICustomerLoanPaymentsPrint,
    APICustomerLoanPaymentsExportExcel,
    APICustomerLoanPaymentsExportPDF,
    APICustomerLoanPaymentDelete,
    APICustomerLoanPaymentUpdate
} from '../../apiServices/APIs';
import { formatCreatedAtDate, recordsCount, recordsPerPage } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { usePermission } from '../../hooks/usePermission';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorrState';
import SystemInformation from '../../components/SystemInformation';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const CustomerLoanPayments = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [selectedPaymentForAction, setSelectedPaymentForAction] = useState(null);
    const [pageState, setPageState] = useState({
        paymentsData: [],
        paymentStats: null,
        selectedPayment: null
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [openView, setOpenView] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [filterOptions, setFilterOptions] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [updateId, setUpdateId] = useState('');

    const [filters, setFilters] = useState({
        paymentMethod: '',
        dateFrom: '',
        dateTo: ''
    });

    // Edit form state
    const [editForm, setEditForm] = useState({
        amount: '',
        payment_method: 'cash',
        notes: '',
        receipt_number: '',
        payment_date: new Date().toISOString().split('T')[0]
    });

    const [editErrors, setEditErrors] = useState({});

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchData();
        fetchPaymentStats();
    }, [showToast]);

    // Fetch payments
    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APICustomerLoanPaymentsList);

            if (result.success) {
                const payments = Array.isArray(result.data) ? result.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    paymentsData: payments
                }));
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load payment data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch payment statistics
    const fetchPaymentStats = async () => {
        setStatsLoading(true);
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APICustomerLoanPaymentsStats);
            if (result.success && result.data) {
                setPageState(prev => ({
                    ...prev,
                    paymentStats: result.data
                }));
            }
        } catch (error) {
            console.error("Error fetching payment statistics:", error);
        } finally {
            setStatsLoading(false);
        }
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const handlePrint = async (record) => {
        if (!hasPermission('PRINT')) {
            showToast("Permission Denied", "You don't have permission to print payment details", "error");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            const result = await apiService.print(APICustomerLoanPaymentsPrint, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file generated successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            showToast("Error", "Failed to generate PDF file", "error");
        }
    };

    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export payment data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            if (filters.paymentMethod) formDataToSend.append('payment_method', filters.paymentMethod);
            if (filters.dateFrom) formDataToSend.append('date_from', filters.dateFrom);
            if (filters.dateTo) formDataToSend.append('date_to', filters.dateTo);
            
            const result = await apiService.exportToExcel(APICustomerLoanPaymentsExportExcel, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message || "Failed to export Excel file", "error");
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export payment data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            if (filters.paymentMethod) formDataToSend.append('payment_method', filters.paymentMethod);
            
            const result = await apiService.exportToPDF(APICustomerLoanPaymentsExportPDF, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message || "Failed to export PDF file", "error");
        }
    };

    // Filter based on search term and filters
    const filteredData = pageState.paymentsData.filter(payment => {
        const matchesSearch = searchTerm === '' ||
            payment.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            payment.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            payment.id?.toString().includes(searchTerm);

        const matchesPaymentMethod = !filters.paymentMethod ||
            payment.payment_method === filters.paymentMethod;

        let matchesDate = true;
        if (filters.dateFrom) {
            const paymentDate = new Date(payment.payment_date || payment.created_at).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && paymentDate >= filterFromDate;
        }

        if (filters.dateTo) {
            const paymentDate = new Date(payment.payment_date || payment.created_at).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && paymentDate <= filterToDate;
        }

        return matchesSearch && matchesPaymentMethod && matchesDate;
    });

    // Calculate totals for filtered data
    const calculateFilteredTotals = () => {
        const totalAmount = filteredData.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
        const totalCount = filteredData.length;

        return {
            totalAmount,
            totalCount
        };
    };

    const filteredTotals = calculateFilteredTotals();

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handleFilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenView = (payment) => {
        setSelectedRecord(payment);
        setOpenView(true);
        handleActionMenuClose();
    };

    const handleCloseView = () => {
        setOpenView(false);
        setSelectedRecord(null);
    };

    const handleOpenEdit = (payment) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to edit payments", "error");
            return;
        }

        setSelectedRecord(payment);
        setUpdateId(payment.id);
        setEditForm({
            amount: payment.amount || '',
            payment_method: payment.payment_method || 'cash',
            notes: payment.notes || '',
            receipt_number: payment.receipt_number || '',
            payment_date: payment.payment_date || new Date().toISOString().split('T')[0]
        });
        setOpenEdit(true);
        handleActionMenuClose();
    };

    const handleCloseEdit = () => {
        setOpenEdit(false);
        setEditErrors({});
        setSelectedRecord(null);
    };

    const handleOpenDelete = (payment) => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete payments", "error");
            return;
        }

        setSelectedRecord(payment);
        setUpdateId(payment.id);
        setOpenDelete(true);
        handleActionMenuClose();
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
        setSelectedRecord(null);
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Edit form handlers
    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({
            ...prev,
            [name]: value
        }));

        if (editErrors[name]) {
            setEditErrors(prev => ({
                ...prev,
                [name]: false
            }));
        }
    };

    const validateEditForm = () => {
        if (!editForm.amount || parseFloat(editForm.amount) <= 0) {
            showToast("Validation Error", "Amount must be greater than 0", "warning");
            return false;
        }
        if (!editForm.payment_method) {
            showToast("Validation Error", "Payment method is required", "warning");
            return false;
        }
        return true;
    };

    const handleUpdatePayment = async (e) => {
        e.preventDefault();

        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update payments", "error");
            return;
        }

        if (!validateEditForm()) return;

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        formDataToSend.append('updateId', updateId);
        formDataToSend.append('amount', editForm.amount);
        formDataToSend.append('payment_method', editForm.payment_method);
        formDataToSend.append('notes', editForm.notes || '');
        formDataToSend.append('receipt_number', editForm.receipt_number || '');
        formDataToSend.append('payment_date', editForm.payment_date);

        try {
            const result = await apiService.update(APICustomerLoanPaymentUpdate, formDataToSend);

            if (result && result.success) {
                showToast("Success", "Payment updated successfully", "success");
                handleCloseEdit();
                fetchData();
                fetchPaymentStats();
            } else {
                showToast("Error", result.message || "Failed to update payment", "error");
            }
        } catch (error) {
            console.error("API Error:", error);
            showToast("Error", "Failed to update payment", "error");
        }
    };

    const handleDeletePayment = async () => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete payments", "error");
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        formDataToSend.append('updateId', updateId);

        try {
            const result = await apiService.delete(APICustomerLoanPaymentDelete, formDataToSend);

            if (result && result.success) {
                showToast("Success", "Payment deleted successfully", "success");
                handleCloseDelete();
                fetchData();
                fetchPaymentStats();
            } else {
                showToast("Error", result.message || "Failed to delete payment", "error");
            }
        } catch (error) {
            console.error("API Error:", error);
            showToast("Error", "Failed to delete payment", "error");
        }
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        handleCloseOptions();
    };

    const applyThisMonthFilter = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

        setFilters({
            ...filters,
            dateFrom: firstDay,
            dateTo: lastDay
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            paymentMethod: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const applyFilters = () => {
        handleCloseOptions();
    };

    // Action menu handlers
    const handleActionMenuOpen = (event, payment) => {
        event.stopPropagation();
        setActionMenuAnchorEl(event.currentTarget);
        setSelectedPaymentForAction(payment);
    };

    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
        setSelectedPaymentForAction(null);
    };

    // Get payment method color
    const getPaymentMethodColor = (method) => {
        switch (method) {
            case 'cash': return 'success';
            case 'mobile_money': return 'primary';
            case 'bank': return 'secondary';
            default: return 'default';
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    // Refresh all data
    const handleRefreshAll = () => {
        fetchData();
        fetchPaymentStats();
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return (
            <LoadingState
                message="Loading Payment Data..."
                variant="medical"
                size="large"
                fullPage
            />
        );
    }

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={fetchData}
                title="Unable to Load Payment Data"
                variant="medical"
                fullPage
                retryText="Retry Loading"
            />
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Title */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 4,
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Payment color="primary" sx={{ fontSize: 30, mr: 2 }} />
                    <Typography sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main
                    }}>
                        Customer Loan Payments
                    </Typography>
                </Box>

                <Typography variant="subtitle1" sx={{
                    fontWeight: 'bold',
                    color: theme.palette.primary.main,
                    flexShrink: 0,
                    width: { xs: '100%', sm: 'auto' },
                    mb: { xs: 1, sm: 0 }
                }}>
                    {recordsCount(pageState.paymentsData.length)} Total Payments
                </Typography>
            </Box>

            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Total Payments Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Payments</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        (pageState.paymentStats?.total_payments || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}>
                                <Paid sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                {/* Total Amount Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        formatCurrency(pageState.paymentStats?.total_amount || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.info.light, width: 56, height: 56 }}>
                                <Money sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                {/* Today's Payments Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Today's Payments</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        (pageState.paymentStats?.today_payments || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 56, height: 56 }}>
                                <TodayIcon sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                {/* Average Payment Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Average Payment</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        formatCurrency(pageState.paymentStats?.average_payment_amount || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 56, height: 56 }}>
                                <TrendingUp sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Search and Filter Bar */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Payment Management
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handleFilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                View Options
                            </Button>
                        )}

                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                Export Data
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefreshAll}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                            size="small"
                        >
                            Refresh
                        </Button>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search payments..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.paymentMethod || filters.dateFrom || filters.dateTo) && (
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.paymentMethod && (
                            <Chip
                                label={`Method: ${filters.paymentMethod.replace('_', ' ').toUpperCase()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, paymentMethod: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon fontSize="small" />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
            </Menu>

            {/* Action Menu for List Items */}
            <Menu
                anchorEl={actionMenuAnchorEl}
                open={Boolean(actionMenuAnchorEl)}
                onClose={handleActionMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {hasPermission('VIEW') && (
                    <MenuItem onClick={() => selectedPaymentForAction && handleOpenView(selectedPaymentForAction)}>
                        <ListItemIcon><ViewIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'primary.main' }}>View Details</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('PRINT') && (
                    <MenuItem onClick={() => selectedPaymentForAction && handlePrint(selectedPaymentForAction)}>
                        <ListItemIcon><Print fontSize="small" color="warning" /></ListItemIcon>
                        <ListItemText sx={{ color: 'warning.main' }}>Print</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('EDIT') && (
                    <MenuItem onClick={() => selectedPaymentForAction && handleOpenEdit(selectedPaymentForAction)}>
                        <ListItemIcon><EditIcon fontSize="small" color="secondary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'secondary.main' }}>Edit</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('DELETE') && (
                    <MenuItem onClick={() => selectedPaymentForAction && handleOpenDelete(selectedPaymentForAction)}>
                        <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {/* Payments Table */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell>Method</TableCell>
                                    <TableCell>Receipt #</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((payment, index) => (
                                    <TableRow
                                        key={payment.id}
                                        hover
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Chip
                                                label={page * rowsPerPage + index + 1}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CalendarMonth color="action" sx={{ mr: 1, fontSize: 16 }} />
                                                {formatCreatedAtDate(payment.payment_date || payment.created_at)}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'medium' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Person color="action" sx={{ mr: 1, fontSize: 16 }} />
                                                {payment.customer_name}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={<Phone />}
                                                label={payment.customer_phone || 'N/A'}
                                                color="primary"
                                                variant="outlined"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                            {formatCurrency(payment.amount)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={payment.payment_method?.toUpperCase()}
                                                color={getPaymentMethodColor(payment.payment_method)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {payment.receipt_number || 'N/A'}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Actions">
                                                <IconButton 
                                                    sx={{ color: theme.palette.action.main }}
                                                    onClick={(e) => handleActionMenuOpen(e, payment)}
                                                    size="small"
                                                >
                                                    <MoreVert />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            
                            {/* Footer with Totals */}
                            {filteredData.length > 0 && (
                                <TableBody>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.light + '20',
                                        borderTop: '3px solid',
                                        borderTopColor: theme.palette.primary.main,
                                        '& td': {
                                            fontWeight: 'bold',
                                            py: 2.5
                                        }
                                    }}>
                                        <TableCell colSpan={4} align="right">
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                Totals ({filteredTotals.totalCount} payments):
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                                {formatCurrency(filteredTotals.totalAmount)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell colSpan={3}>
                                            <Chip
                                                icon={<Money />}
                                                label={`Total Amount: ${formatCurrency(filteredTotals.totalAmount)}`}
                                                size="small"
                                                color="primary"
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredData.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <Payment sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No payment records found</Typography>
                            <Typography variant="body2">Try adjusting your search or filter criteria</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* View Payment Details Dialog */}
            <Dialog 
                open={openView} 
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseView();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Payment Details - #{selectedRecord?.id}</b>
                </DialogTitle>
                
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                mb: 3,
                                p: 2,
                                backgroundColor: theme.palette.grey[100],
                                borderRadius: 2
                            }}>
                                <Avatar sx={{ 
                                    backgroundColor: theme.palette.primary.light,
                                    color: theme.palette.primary.main,
                                    mr: 2
                                }}>
                                    <Person />
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        {selectedRecord.customer_name}
                                    </Typography>
                                    <Typography variant="body2">
                                        <Phone sx={{ verticalAlign: 'middle', fontSize: 16, mr: 0.5 }} />
                                        {selectedRecord.customer_phone || 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={6}>
                                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                        <Typography variant="body2" color="text.secondary">Payment Amount</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                            {formatCurrency(selectedRecord.amount)}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                        <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                                        <Chip
                                            label={selectedRecord.payment_method?.toUpperCase()}
                                            color={getPaymentMethodColor(selectedRecord.payment_method)}
                                            size="medium"
                                            sx={{ mt: 1 }}
                                        />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                        <Typography variant="body2" color="text.secondary">Receipt Number</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            {selectedRecord.receipt_number || 'N/A'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                        <Typography variant="body2" color="text.secondary">Payment Date</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            {formatCreatedAtDate(selectedRecord.payment_date || selectedRecord.created_at)}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                        <Typography variant="body2" color="text.secondary">Notes</Typography>
                                        <Typography variant="body1">
                                            {selectedRecord.notes || 'No notes provided'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* System Information - Card Style */}
                            <SystemInformation
                                createdBy={selectedRecord.created_by}
                                updatedBy={selectedRecord.updated_by}
                                createdAt={selectedRecord.created_at}
                                updatedAt={selectedRecord.updated_at}
                                title="Payments System Information"
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    {hasPermission('EDIT') && selectedRecord && (
                        <Button
                            onClick={() => {
                                handleCloseView();
                                handleOpenEdit(selectedRecord);
                            }}
                            color="warning"
                            variant="contained"
                            startIcon={<EditIcon />}
                        >
                            Edit
                        </Button>
                    )}
                    {hasPermission('PRINT') && selectedRecord && (
                        <Button onClick={() => {
                            handlePrint(selectedRecord);
                        }} color="info" variant="contained">
                            <Print sx={{ mr: 1 }} /> Print
                        </Button>
                    )}
                    <Button onClick={handleCloseView} color="error" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Payment Dialog */}
            <Dialog
                open={openEdit}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseEdit();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Edit Payment - #{selectedRecord?.id}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{ mt: 2 }}>
                        {selectedRecord && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    Customer: <strong>{selectedRecord.customer_name}</strong>
                                </Typography>
                                <Typography variant="body2">
                                    Current Amount: <strong>{formatCurrency(selectedRecord.amount)}</strong>
                                </Typography>
                            </Alert>
                        )}
                        
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>
                            Payment Details
                        </Typography>
                        
                        <TextField
                            fullWidth
                            label="Payment Amount *"
                            name="amount"
                            type="number"
                            value={editForm.amount}
                            onChange={handleEditFormChange}
                            required
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AttachMoney />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Payment Method *</InputLabel>
                            <Select
                                name="payment_method"
                                value={editForm.payment_method}
                                onChange={handleEditFormChange}
                                label="Payment Method *"
                            >
                                <MenuItem value="cash">Cash</MenuItem>
                                <MenuItem value="mobile_money">Mobile Money</MenuItem>
                                <MenuItem value="bank">Bank</MenuItem>
                            </Select>
                        </FormControl>
                        
                        <TextField
                            fullWidth
                            label="Receipt Number (Optional)"
                            name="receipt_number"
                            value={editForm.receipt_number}
                            onChange={handleEditFormChange}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Receipt />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        
                        <TextField
                            fullWidth
                            label="Payment Date"
                            name="payment_date"
                            type="date"
                            value={editForm.payment_date}
                            onChange={handleEditFormChange}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarMonth />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        
                        <TextField
                            fullWidth
                            label="Notes (Optional)"
                            name="notes"
                            value={editForm.notes}
                            onChange={handleEditFormChange}
                            multiline
                            rows={3}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Note />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseEdit}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdatePayment}
                        color="primary"
                        variant="contained"
                    >
                        Update Payment
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDelete}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDelete();
                    }
                }}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Payment Record</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this payment record?
                    </Typography>
                    {selectedRecord && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {selectedRecord.customer_name}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                <Chip
                                    label={formatCurrency(selectedRecord.amount)}
                                    color="success"
                                    variant="outlined"
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                <Chip
                                    label={selectedRecord.payment_method?.toUpperCase()}
                                    color={getPaymentMethodColor(selectedRecord.payment_method)}
                                    variant="outlined"
                                    size="small"
                                />
                            </Box>
                            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                Warning: This action cannot be undone!
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseDelete} variant="contained">
                        Cancel
                    </Button>
                    <Button onClick={handleDeletePayment} color="primary" variant="contained">
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Payments
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Payments
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CalendarMonth />}
                            onClick={applyThisMonthFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show This Month's Payments
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Fields
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Payment Method</InputLabel>
                            <Select
                                name="paymentMethod"
                                value={filters.paymentMethod}
                                onChange={handleFilterChange}
                                label="Payment Method"
                            >
                                <MenuItem value="">All Methods</MenuItem>
                                <MenuItem value="cash">Cash</MenuItem>
                                <MenuItem value="mobile_money">Mobile Money</MenuItem>
                                <MenuItem value="bank">Bank</MenuItem>
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">
                        Close
                    </Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CustomerLoanPayments;